import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { DefaultError } from "@/components/handle-query";
import { Skeleton } from "@/components/skeleton";
import type { useReferredVolumeQuery } from "@/contexts/queries/referred-volume";

import { SectionCard } from "../../components";
import { usdCompact } from "../../format";
import { formatDayLabel } from "../../points/derive";
import { toReferredVolumeBars } from "../referred-volume";

const WINDOW_DAYS = 14;

/**
 * Volume traded per day by the affiliate's network.
 *
 * Pairs with the funnel card: that one measures acquisition (sign-ups, deposits),
 * this one measures activation — whether those referrals actually trade, which is what
 * drives the affiliate's earnings.
 *
 * Single series, so no legend; the card title names what is plotted. Column hue is
 * brandYellow.550, which clears 3:1 against the card surface.
 *
 * The figure is both-sides notional. It shares its method with the "Referred volume"
 * KPI above (`account_trading_stats.total_volume`, likewise unfiltered by side), so the
 * two agree — the KPI is all-time while this is a 14-day window.
 */
export function ReferredVolumeDailyChart({
  query,
}: {
  query: ReturnType<typeof useReferredVolumeQuery>;
}) {
  const t = useTranslations("affiliates.overview.volumeDailyChart");

  if (query.isError) {
    return (
      <SectionCard label={t("window")} title={t("title")} fill>
        <DefaultError error={query.error} resetErrorBoundary={() => void query.refetch()} />
      </SectionCard>
    );
  }

  if (query.data === undefined) {
    return (
      <SectionCard label={t("window")} title={t("title")} fill>
        <Skeleton className={css({ w: "full", h: "[160px]", rounded: "200" })} />
      </SectionCard>
    );
  }

  const bars = toReferredVolumeBars(query.data, WINDOW_DAYS);

  if (bars.length === 0) {
    return (
      <SectionCard label={t("window")} title={t("title")} fill>
        <div className={css({ textStyle: "body.md", color: "text.tertiary" })}>{t("empty")}</div>
      </SectionCard>
    );
  }

  const total = bars.reduce((sum, bar) => sum + bar.volumeUsd, 0);
  // Floored at 1 so an all-zero window renders flat instead of dividing by zero.
  const max = Math.max(...bars.map((bar) => bar.volumeUsd), 1);

  return (
    <SectionCard
      label={t("window")}
      title={t("title")}
      rightSlot={
        <span className={css({ textStyle: "body.md", color: "text.tertiary" })}>
          {t("total", {
            amount: usdCompact(total),
          })}
        </span>
      }
      fill
    >
      <div className={stack({ gap: "8", minW: "0", flex: "1" })}>
        <div className={chartAreaStyles}>
          {bars.map((bar) => (
            <div
              key={bar.dayStartUnixMs}
              className={barCellStyles}
              title={t("barTooltip", {
                label: formatDayLabel(bar.dayStartUnixMs),
                amount: usdCompact(bar.volumeUsd),
                traders: bar.activeReferrals,
              })}
            >
              <div className={barStyles} style={{ height: `${(bar.volumeUsd / max) * 100}%` }} />
            </div>
          ))}
        </div>
        <div className={flex({ justifyContent: "space-between", gap: "8" })}>
          <span className={axisLabelStyles}>{formatDayLabel(bars[0]?.dayStartUnixMs ?? 0)}</span>
          {bars.length > 1 && (
            <span className={axisLabelStyles}>
              {formatDayLabel(bars[bars.length - 1]?.dayStartUnixMs ?? 0)}
            </span>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

const chartAreaStyles = flex({
  alignItems: "flex-end",
  gap: "4",
  flex: "1",
  minH: "[120px]",
  w: "full",
});

const barCellStyles = css({
  flex: "1",
  display: "flex",
  alignItems: "flex-end",
  minW: "0",
  h: "full",
});

const barStyles = css({
  w: "full",
  // Rounded cap, square at the baseline. minHeight keeps a zero day on the axis.
  roundedTop: "200",
  minH: "[2px]",
  bg: "brandYellow.550",
});

const axisLabelStyles = css({
  textStyle: "body.sm",
  color: "text.tertiary",
});
